import { Injectable, isDevMode, OnInit } from "@angular/core";
import { ColDef, ColumnState } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";

@Injectable({
  providedIn: 'root'
})
export class PreferenceValidationService implements OnInit {
  constructor(
    private readonly toastr: ToastrService
  ) { }

  ngOnInit(): void {
  }

  public validatePreferences(preferences: ColumnState[], defaultColumnDefs: ColDef[]): ColumnState[] {

    // Major/unfixable issues
    // We don't want to save empty preferences, so we reset to defaults instead.
    if (this.preferenceIsEmpty(preferences)) {
      if (isDevMode()) {
        this.toastr?.error('Tried to save empty preferences, reset to default.', '', { toastClass: 'ngx-toastr toast-error tall-toastr' });
      }
      return this.constructValidPreferencesFromDefault(defaultColumnDefs);
    }

    // We don't want to save preferences with missing/extra columns, so we reset to defaults instead.
    if (this.columnCountMismatch(preferences, defaultColumnDefs)) {
      if (isDevMode()) {
        this.toastr?.error('Tried to save preferences with incorrect column count, reset to default.', '', { toastClass: 'ngx-toastr toast-error tall-toastr' });
      }
      return this.constructValidPreferencesFromDefault(defaultColumnDefs);
    }

    // We want to make sure that all saved columns have names, and that the names match those of the definitions.
    if (this.columnsAreNamedImproperly(preferences, defaultColumnDefs)) {
      if (isDevMode()) {
        this.toastr?.error('Tried to save preferences with improperly named columns, reset to default.', '', { toastClass: 'ngx-toastr toast-error tall-toastr' });
      }
      return this.constructValidPreferencesFromDefault(defaultColumnDefs);
    }

    // Minor/fixable issues
    // We only ever want one column to be sorted at a time, so if more than one is we reset to the default sorted column.
    if (this.multipleColumnsAreSorted(preferences)) {
      if (isDevMode()) {
        this.toastr?.error('Tried to save preferences with too many sorted columns, reset to default sort.', '', { toastClass: 'ngx-toastr toast-error tall-toastr' });
      }
      return this.revertPreferencesSortToDefault(preferences, defaultColumnDefs);
    }

    return preferences;
  }

  private preferenceIsEmpty(unvalidatedPreference: ColumnState[]): boolean {
    if (!unvalidatedPreference || unvalidatedPreference.length === 0) {
      return true;
    }
    return false;
  }

  private columnCountMismatch(unvalidatedPreference: ColumnState[], defaultColumnDefs: ColDef[]): boolean {
    if (unvalidatedPreference.length !== defaultColumnDefs.length) {
      return true
    }
    return false;
  }

  private columnsAreNamedImproperly(unvalidatedPreference: ColumnState[], defaultColumnDefs: ColDef[]): boolean {

    let isInvalid: boolean = false;

    defaultColumnDefs.forEach(defaultColumn => {
      let columnFound = undefined;
      columnFound = unvalidatedPreference.find(preferenceColumn => {
        return defaultColumn.field === preferenceColumn.colId;
      });
      if (columnFound === undefined) {
        isInvalid = true;
      }
    });

    return isInvalid;
  }

  private multipleColumnsAreSorted(unvalidatedPreference: ColumnState[]): boolean {
    let sortCount: number = 0;

    unvalidatedPreference.forEach(column => {
      if (column.sort !== null) {
        sortCount++;
      }
    });

    if (sortCount > 1) {
      return true;
    }
    return false;
  }

  private constructValidPreferencesFromDefault(defaultColDef: ColDef[]): ColumnState[] {
    let defaultedColumnState: ColumnState[] = [];

    defaultColDef.forEach(defaultColumn => {
      const sort = defaultColumn.sort;
      const sortValue = typeof sort === 'string' ? sort : null;

      let column: ColumnState = {
        colId: defaultColumn.field ?? '',
        width: defaultColumn.width ?? defaultColumn.minWidth,
        hide: defaultColumn.hide ?? false,
        pinned: defaultColumn.pinned ?? false,
        sort: sortValue,
        sortIndex: defaultColumn.sortIndex ?? null,
        aggFunc: defaultColumn.aggFunc ?? '',
        rowGroup: defaultColumn.rowGroup ?? false,
        rowGroupIndex: defaultColumn.rowGroupIndex ?? null,
        pivot: defaultColumn.pivot ?? false,
        pivotIndex: defaultColumn.pivotIndex ?? null,
        flex: defaultColumn.flex ?? 1
      }
      defaultedColumnState.push(column);
    });

    return defaultedColumnState;
  }

  private revertPreferencesSortToDefault(preferences: ColumnState[], defaultColDef: ColDef[]): ColumnState[] {
    let fixedColumnState: ColumnState[] = [];

    preferences.forEach(preferenceColumn => {
      const defaultSort = defaultColDef.find(defaultColumn => {
        return defaultColumn.field === preferenceColumn.colId;
      })?.sort;

      const sortValue = typeof defaultSort === 'string' ? defaultSort : null;

      let column: ColumnState = {
        colId: preferenceColumn.colId,
        width: preferenceColumn.width,
        hide: preferenceColumn.hide,
        pinned: preferenceColumn.pinned,
        sort: sortValue,
        sortIndex: preferenceColumn.sortIndex,
        aggFunc: preferenceColumn.aggFunc,
        rowGroup: preferenceColumn.rowGroup,
        rowGroupIndex: preferenceColumn.rowGroupIndex,
        pivot: preferenceColumn.pivot,
        pivotIndex: preferenceColumn.pivotIndex,
        flex: preferenceColumn.flex
      }
      fixedColumnState.push(column);
    });

    return fixedColumnState;
  }
}
